import type {
  SavingsCashIllustration,
  SavingsCashIllustrationCreatePayload,
  SavingsCashIllustrationEstimate,
  SavingsCashIllustrationEstimatePayload,
  SavingsCashIllustrationProductOption
} from '@/interfaces/SavingsCashIllustration.interface';
import type { SavingsProductListItem } from '@/interfaces/SavingsProduct.interface';
import { filenameFromContentDisposition } from '../savings-products/response-helpers';
import BaseApiClient from '../BaseApiClient';
import {
  SAVINGS_API_VERSION_HEADERS,
  toSavingsListResponse
} from '../savings-reference-resource';
import type {
  ListDataParams,
  ListDataResponse,
  ListExportParams
} from '../types';

const PRODUCT_LOOKUP_PAGE_SIZE = 25;

/** Saved illustrations are read-only: they can be created and browsed, never edited. */
export class ApiSavingsCashIllustrations extends BaseApiClient {
  private readonly path = '/savings-cash-illustrations';

  /** Filter and sort keys map one-to-one onto the API's; any other key is rejected with a 400. */
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsCashIllustration>> {
    const search = searchTerm?.trim();
    const res = await this.client.getList(
      this.path,
      {
        ...listData,
        filters: { ...listData.filters, ...(search ? { search } : {}) }
      },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsCashIllustration>(res.data);
  }

  async get(id: number): Promise<SavingsCashIllustration> {
    const res = await this.client.get(`${this.path}/${id}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** The API calculates each line's interest; the payload never carries it. */
  async create(
    payload: SavingsCashIllustrationCreatePayload
  ): Promise<SavingsCashIllustration> {
    const res = await this.client.post(this.path, payload, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return res.data.data;
  }

  /** The same estimate creation would save, without saving anything. */
  async estimate(
    payload: SavingsCashIllustrationEstimatePayload
  ): Promise<SavingsCashIllustrationEstimate> {
    const res = await this.client.post(
      '/savings-cash-illustration-estimates',
      payload,
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return res.data.data;
  }

  /**
   * Active products the illustration currency allows, matching the API's currency check.
   * Each rate change leaves the old issue behind as an inactive product with the same bank
   * and name, so without the status filter one product appears many times.
   */
  async listProducts(
    currency: string,
    searchTerm?: string
  ): Promise<SavingsCashIllustrationProductOption[]> {
    const search = searchTerm?.trim();
    const res = await this.client.getList(
      '/savings-products',
      {
        page: 1,
        perPage: PRODUCT_LOOKUP_PAGE_SIZE,
        filters: { currency, status: 'active', ...(search ? { search } : {}) }
      },
      { headers: SAVINGS_API_VERSION_HEADERS }
    );
    return toSavingsListResponse<SavingsProductListItem>(res.data).items.map(
      (product) => ({
        id: product.id,
        name: product.name,
        bank_name: product.savings_bank?.name ?? null,
        currency: product.currency,
        product_type: product.savings_product_type?.name ?? null,
        withdrawal_notice_days: product.withdrawal_notice_days,
        fixed_term: product.fixed_term
      })
    );
  }

  async getPdf(id: number): Promise<{ blob: Blob; filename: string }> {
    const res = await this.client.get(`${this.path}/${id}/pdf`, {
      responseType: 'blob',
      headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'application/pdf' }
    });
    return {
      blob: new Blob([res.data], { type: 'application/pdf' }),
      filename: filenameFromContentDisposition(
        res.headers['content-disposition'],
        `savings-cash-illustration-${id}.pdf`
      )
    };
  }

  /** Exports every row matching the filters and search, in the table's order, independently of the page shown. */
  async export(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const search = searchTerm?.trim();
    const response = await this.client.getList(
      `${this.path}/export`,
      {
        filters: { ...listData.filters, ...(search ? { search } : {}) },
        sort: listData.sort
      },
      {
        responseType: 'blob',
        headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'text/csv' }
      }
    );

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        'savings-cash-illustrations.csv'
      )
    };
  }
}

export { savingsCashIllustrationsQueryKeys } from './query-keys';
export {
  useSavingsCashIllustrationEstimateQuery,
  useSavingsCashIllustrationProductsQuery,
  useSavingsCashIllustrationQuery
} from './use-savings-cash-illustrations-queries';
export {
  useSavingsCashIllustrationCreateMutation,
  useSavingsCashIllustrationPdfMutation,
  useSavingsCashIllustrationsExportMutation
} from './use-savings-cash-illustrations-mutations';
export type { SavingsCashIllustrationsExportVariables } from './use-savings-cash-illustrations-mutations';
